import {
  getNotificationSubscriptionId,
  Notification,
  notificationBaseOps,
  NotificationSubscription,
  notificationSubscriptionBaseOps,
} from '@basehub/data/replicache/index'
import { ReadTransaction, WriteTransaction } from 'replicache'
import { SetOptional } from 'type-fest'
import { WithServerSecret } from '../../../tx/spec'
import { invariant } from '@epic-web/invariant'
import { hashJson } from '@basehub/utils/hash'
import { defaultLength } from '@basehub/utils/id/index'
import { ensureBaseHubTransaction } from '@basehub/data/replicache/tx/iso'

export type UpdateNotificationSubscriptionData = {
  notificationSubscription:
    | SetOptional<NotificationSubscription, 'id' | 'deletedAt'>
    | SetOptional<NotificationSubscription, 'id'>[]
  notificationToSend: null | SetOptional<Notification, 'userId' | 'id'>
}

const updateNotificationSubscription = async (
  tx: WriteTransaction,
  data: WithServerSecret<
    {
      idSeed: string
      isoNow: string
    } & UpdateNotificationSubscriptionData
  >
) => {
  ensureBaseHubTransaction(tx)
  invariant(tx.serverOnly, 'can only be called on the server')

  const subscriptions = Array.isArray(data.notificationSubscription)
    ? data.notificationSubscription
    : [data.notificationSubscription]

  const updatedSubs: NotificationSubscription[] = []

  for (const subscription of subscriptions) {
    const subKey = getNotificationSubscriptionId(subscription)
    const currentSub = await notificationSubscriptionBaseOps.get(tx, subKey)

    if (currentSub && subscription.userIds.length > 0) {
      const updatedSub = {
        ...subscription,
        id: subKey,
        userIds: Array.from(
          new Set([...currentSub.userIds, ...subscription.userIds])
        ),
      }
      await notificationSubscriptionBaseOps.set(tx, updatedSub)
      updatedSubs.push(updatedSub)
    } else if (subscription.userIds.length > 0) {
      const newSub = { ...subscription, id: subKey }
      await notificationSubscriptionBaseOps.set(tx, newSub)
      updatedSubs.push(newSub)
    }
  }

  const notificationToSend = data.notificationToSend

  if (!notificationToSend) {
    return { subs: updatedSubs, isGuest: tx.serverOnly?.isGuest }
  }
  if (updatedSubs.length === 0) return null

  // create notification for each user in all subs
  for (const updatedSub of updatedSubs) {
    await Promise.all(
      updatedSub.userIds.map(async (userId) => {
        if (notificationToSend.authorId === userId) return

        let hashable: Record<string, unknown> = {
          userId,
          authorId: notificationToSend.authorId,
          authorUsername: notificationToSend.authorUsername,
          branchId: notificationToSend.branchId,
          href: notificationToSend.href,
        }

        switch (notificationToSend.type) {
          case 'repo-cr-resolution':
            hashable = {
              ...hashable,
              resolution: notificationToSend.resolution,
            }
            break
          case 'repo-thread-comment':
            hashable = {
              ...hashable,
              commentType: notificationToSend.commentType,
              content: notificationToSend.content,
              rootId: notificationToSend.rootId,
              commentId: notificationToSend.commentId,
            }
            break
          case 'workflow-action': {
            hashable = {
              ...hashable,
              payload: notificationToSend.payload,
            }
            break
          }
          default:
            break
        }

        const newNotificationId = hashJson(hashable).slice(0, defaultLength)
        // check for duplicates
        const existingNotification = await notificationBaseOps.get(
          tx,
          newNotificationId
        )
        if (existingNotification) return

        await notificationBaseOps.set(tx, {
          ...notificationToSend,
          id: newNotificationId,
          userId,
        })
      })
    )
  }

  return { subs: updatedSubs, isGuest: tx.serverOnly?.isGuest }
}

const listNotificationsByUser = async (tx: ReadTransaction, userId: string) => {
  const notifications = await notificationBaseOps.list(tx)
  return notifications.filter((n) => n.userId === userId)
}

const markNotificationRead = async (
  tx: WriteTransaction,
  notificationId: string
) => {
  await notificationBaseOps.update(tx, { id: notificationId, read: true })
}

const markAllNotificationsRead = async (
  tx: WriteTransaction,
  userId: string
) => {
  const notifications = await listNotificationsByUser(tx, userId)
  for (const notification of notifications) {
    await markNotificationRead(tx, notification.id)
  }
}

export const notificationMutators = {
  listNotificationsByUser,
  updateNotificationSubscription,
  markAllNotificationsRead,
  markNotificationRead,
}
